import mixpanel from "mixpanel-browser";

/**
 * Analytics Environment Configuration
 *
 * Manages Mixpanel initialization and configuration across different environments
 * with proper privacy controls, error handling, and debugging capabilities.
 */

export interface AnalyticsConfig {
  token: string | undefined;
  isProduction: boolean;
  isDevelopment: boolean;
  isEmulator: boolean;
  environment: "production" | "staging" | "development" | "emulator";
  debugEnabled: boolean;
  privacyOptOut: boolean;
}

export interface AnalyticsStatus {
  initialized: boolean;
  hasToken: boolean;
  privacyOptOut: boolean;
  environment: string;
  error?: string;
}

// Environment detection
const MIXPANEL_TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN;
const FIREBASE_PROJECT_ID = import.meta.env.VITE_FIREBASE_PROJECT_ID;
const HOST_URL = import.meta.env.VITE_FIREBASE_HOST_URL;

// Privacy settings local storage key
const ANALYTICS_OPT_OUT_KEY = "shophero_analytics_opt_out";

/**
 * Determines the current environment based on project ID and host URL
 */
function getEnvironment(): AnalyticsConfig["environment"] {
  if (HOST_URL?.includes("localhost") || HOST_URL?.includes("127.0.0.1")) {
    return "emulator";
  }

  if (FIREBASE_PROJECT_ID === "shopherodashboard") {
    return "production";
  } else if (FIREBASE_PROJECT_ID?.includes("staging")) {
    return "staging";
  } else if (FIREBASE_PROJECT_ID?.includes("dev")) {
    return "development";
  }

  return "development"; // fallback
}

/**
 * Checks if user has opted out of analytics tracking
 */
function getPrivacyOptOut(): boolean {
  try {
    return localStorage.getItem(ANALYTICS_OPT_OUT_KEY) === "true";
  } catch (error) {
    console.warn("Unable to check analytics opt-out status:", error);
    return false;
  }
}

/**
 * Sets user's analytics opt-out preference
 * @param optOut
 */
export function setAnalyticsOptOut(optOut: boolean): void {
  try {
    if (optOut) {
      localStorage.setItem(ANALYTICS_OPT_OUT_KEY, "true");
      // Reset mixpanel data if opting out
      if (analyticsStatus.initialized) {
        mixpanel.reset();
      }
    } else {
      localStorage.removeItem(ANALYTICS_OPT_OUT_KEY);
    }
    analyticsStatus.privacyOptOut = optOut;

    if (analyticsConfig.debugEnabled) {
      console.log(
        `🔒 Analytics tracking ${optOut ? "disabled" : "enabled"} by user preference`
      );
    }
  } catch (error) {
    console.error("Unable to set analytics opt-out preference:", error);
  }
}

// Configuration setup
const environment = getEnvironment();
const privacyOptOut = getPrivacyOptOut();

export const analyticsConfig: AnalyticsConfig = {
  token: MIXPANEL_TOKEN,
  isProduction: environment === "production",
  isDevelopment: environment === "development",
  isEmulator: environment === "emulator",
  environment,
  debugEnabled: import.meta.env.DEV || environment !== "production",
  privacyOptOut,
};

// Analytics initialization status
export const analyticsStatus: AnalyticsStatus = {
  initialized: false,
  hasToken: Boolean(MIXPANEL_TOKEN),
  privacyOptOut,
  environment,
};

/**
 * Initialize Mixpanel with environment-specific configuration
 */
function initializeMixpanel(): void {
  if (!MIXPANEL_TOKEN) {
    analyticsStatus.error = "No Mixpanel token configured";
    if (analyticsConfig.debugEnabled) {
      console.warn(
        "⚠️ Analytics disabled: No Mixpanel token configured for environment:",
        environment
      );
    }
    return;
  }

  if (privacyOptOut) {
    analyticsStatus.error = "User has opted out of analytics";
    if (analyticsConfig.debugEnabled) {
      console.log("🔒 Analytics disabled: User has opted out of tracking");
    }
    return;
  }

  try {
    // Environment-specific configuration
    const initConfig = {
      debug: analyticsConfig.debugEnabled,
      track_pageview: analyticsConfig.isProduction, // Only track pageviews in production
      persistence: "localStorage" as const,
      secure_cookie: analyticsConfig.isProduction, // Use secure cookies in production
      cross_subdomain_cookie: analyticsConfig.isProduction,
      // Disable tracking in emulator unless explicitly enabled for testing
      disable_all_events:
        analyticsConfig.isEmulator &&
        !import.meta.env.VITE_ANALYTICS_ENABLE_EMULATOR,
      // We really want to track users and they are not allowed to opt out via browser settings
      opt_out_tracking_by_default: false,
      ignore_dnt: true,
    };

    mixpanel.init(MIXPANEL_TOKEN, initConfig);

    analyticsStatus.initialized = true;
    analyticsStatus.error = undefined;

    if (analyticsConfig.debugEnabled) {
      console.log("📊 Analytics initialized successfully:", {
        environment,
        token: MIXPANEL_TOKEN.substring(0, 8) + "...",
        config: initConfig,
      });
    }

    // Set environment properties
    mixpanel.register({
      environment,
      app_version: import.meta.env.VITE_VERSION || "unknown",
      build_mode: import.meta.env.MODE,
    });
  } catch (error) {
    analyticsStatus.error =
      error instanceof Error ? error.message : "Unknown initialization error";
    console.error("❌ Failed to initialize analytics:", error);
  }
}

/**
 * Checks if analytics is properly initialized and ready to use
 */
export function isAnalyticsReady(): boolean {
  return (
    analyticsStatus.initialized &&
    analyticsStatus.hasToken &&
    !analyticsStatus.privacyOptOut &&
    !analyticsStatus.error
  );
}

/**
 * Gets current analytics status for debugging
 */
export function getAnalyticsStatus(): AnalyticsStatus {
  return { ...analyticsStatus };
}

/**
 * Gets current analytics configuration for debugging
 */
export function getAnalyticsConfig(): AnalyticsConfig {
  return { ...analyticsConfig };
}

/**
 * Logs environment information for debugging
 */
export function logEnvironmentInfo(): void {
  if (analyticsConfig.debugEnabled) {
    console.group("📊 Analytics Environment Information");
    console.log("Environment:", environment);
    console.log("Project ID:", FIREBASE_PROJECT_ID);
    console.log("Host URL:", HOST_URL);
    console.log("Has Token:", analyticsStatus.hasToken);
    console.log("Privacy Opt-out:", analyticsStatus.privacyOptOut);
    console.log("Initialized:", analyticsStatus.initialized);
    if (analyticsStatus.error) {
      console.log("Error:", analyticsStatus.error);
    }
    console.groupEnd();
  }
}

// Initialize analytics on module load
initializeMixpanel();

// Log environment info in development
if (analyticsConfig.debugEnabled) {
  logEnvironmentInfo();
}

export const analytics = mixpanel;
